import { memo } from 'react';
import { clsx } from 'clsx';

interface HeatmapDay {
  date: string;
  value: number;
}

interface HeatmapCalendarProps {
  data: HeatmapDay[];
  weeks?: number;
  colorScale?: 'primary' | 'success' | 'warn';
  className?: string;
}

export const HeatmapCalendar = memo(function HeatmapCalendar({
  data, weeks = 12, colorScale = 'primary', className = '',
}: HeatmapCalendarProps) {
  // Build a 7-row x N-col grid from the last N weeks
  const today = new Date();
  const startDate = new Date(today);
  startDate.setDate(startDate.getDate() - (weeks * 7));

  const dataMap = new Map(data.map(d => [d.date, d.value]));
  const max = Math.max(...data.map(d => d.value), 1);

  const cells: Array<{ date: string; level: number }> = [];
  const d = new Date(startDate);
  // Align to start of week (Sunday)
  d.setDate(d.getDate() - d.getDay());

  while (d <= today) {
    const key = d.toISOString().slice(0, 10);
    const val = dataMap.get(key) ?? 0;
    const level = val === 0 ? 0 : Math.min(4, Math.ceil((val / max) * 4));
    cells.push({ date: key, level });
    d.setDate(d.getDate() + 1);
  }

  const colorMap: Record<string, string[]> = {
    primary: ['bg-surface-2', 'bg-primary/20', 'bg-primary/40', 'bg-primary/60', 'bg-primary/80'],
    success: ['bg-surface-2', 'bg-success/20', 'bg-success/40', 'bg-success/60', 'bg-success/80'],
    warn: ['bg-surface-2', 'bg-warn/20', 'bg-warn/40', 'bg-warn/60', 'bg-warn/80'],
  };
  const colors = (colorMap[colorScale] ?? colorMap.primary) as string[];

  // Arrange into columns (weeks) with 7 rows each
  const columns: typeof cells[] = [];
  for (let i = 0; i < cells.length; i += 7) {
    columns.push(cells.slice(i, i + 7));
  }

  return (
    <div className={clsx('flex gap-[3px]', className)}>
      {columns.map((col, ci) => (
        <div key={ci} className="flex flex-col gap-[3px]">
          {col.map((cell, ri) => (
            <div
              key={ri}
              className={clsx('w-[10px] h-[10px] rounded-[2px] transition-colors', colors[cell.level])}
              title={`${cell.date}: ${dataMap.get(cell.date) ?? 0}`}
            />
          ))}
        </div>
      ))}
    </div>
  );
});
